import _ from "lodash";
import { CommandPayload } from "src/data/commands";

import genericPolicyRules from "./common";
import { CommandShape, CompositePolicyRule, PolicyRule, RuleCategory, RuleValues } from "./types";

export type MatchedPolicyRule = { rule: PolicyRule; values: RuleValues };

export type RuleCategoryFilter = RuleCategory | "all";

type Shape = Record<string, string>;

/**
 * The ready made rules the user can pick in the "Add rule" dialog.
 * Each rule describes its commands, and this class does all the work of building and reading them.
 */
class RuleCatalog {
    public static readonly rules: PolicyRule[] = [...genericPolicyRules];

    public static readonly allCategories = "all";

    public static get categories(): RuleCategoryFilter[] {
        return [this.allCategories, ..._.orderBy(_.uniq(this.rules.flatMap((rule) => rule.categories)))];
    }

    private static isComposite(rule: PolicyRule): rule is CompositePolicyRule {
        return "subcommands" in rule.command;
    }

    private static isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === "object" && value !== null && !Array.isArray(value);
    }

    private static matchesShapeValue(key: string, actual: unknown, expected: string): boolean {
        return key === "section"
            ? typeof actual === "string" && actual.toLowerCase() === expected.toLowerCase()
            : actual === expected;
    }

    private static matchShape(
        command: unknown,
        shape: Shape,
        valueKey: string,
        ignoredKeys: string[] = [],
    ): Record<string, unknown> | null {
        if (!this.isRecord(command)) {
            return null;
        }

        // The backend may add an `id` to every policy entry, and the caption is only a display name.
        const allowedKeys = new Set([...Object.keys(shape), valueKey, "caption", "id", ...ignoredKeys]);
        const hasShape = Object.entries(shape).every(([key, expected]) => this.matchesShapeValue(key, command[key], expected));
        const hasOnlyAllowedKeys = Object.keys(command).every((key) => allowedKeys.has(key));

        return hasShape && valueKey in command && hasOnlyAllowedKeys ? command : null;
    }

    /**
     * Pairs each shape with the subcommand that matches it, leaving a shape the command omits as `undefined`.
     * Returns null when a subcommand matches no shape, so a command carrying anything extra stays a custom one.
     */
    private static matchSubcommands(subcommands: unknown[], shapes: Shape[]): unknown[] | null {
        const unmatched = [...subcommands.keys()];

        const values = shapes.map((shape) => {
            const position = unmatched.findIndex((index) => this.matchShape(subcommands[index], shape, "value"));
            if (position === -1) {
                return undefined;
            }
            const [index] = unmatched.splice(position, 1);
            return (subcommands[index] as Record<string, unknown>).value;
        });

        return unmatched.length === 0 ? values : null;
    }

    /**
     * Reads the values a command was created with, as the rule's own `fromValues` returns them.
     * Returns null when the command does not match the rule.
     */
    private static readRule(command: Record<string, unknown>, rule: PolicyRule): Record<string, unknown> | null {
        if (!this.isComposite(rule)) {
            const match = this.matchShape(command, rule.command, "value", rule.ignoredKeys);
            return match && rule.fromValues(match.value);
        }

        const { subcommands: shapes, ...wrapper } = rule.command;
        const match = this.matchShape(command, wrapper, "subcommands", rule.ignoredKeys);
        const subcommands = match?.subcommands;
        if (!Array.isArray(subcommands) || subcommands.length > shapes.length) {
            return null;
        }

        const values = this.matchSubcommands(subcommands, shapes);
        return values && rule.fromValues(values);
    }

    /**
     * Finds the rule that describes a command, and the values that were used to create it.
     * Returns null when no rule fits, so the command is shown as a custom one.
     */
    public static fromCommand(command: Record<string, unknown>): MatchedPolicyRule | null {
        for (const rule of this.rules) {
            const rawValues = this.readRule(command, rule);
            const parsed = rawValues ? rule.schema.safeParse(rawValues) : null;
            if (parsed?.success) {
                return { rule, values: parsed.data };
            }
        }

        return null;
    }

    /**
     * Returns the positions of commands whose rule appears more than once although it does not allow duplicates.
     * Commands no rule describes are never reported.
     */
    public static getDuplicatedIndexes(commands: Record<string, unknown>[]): Set<number> {
        const indexesByRule = new Map<string, number[]>();

        commands.forEach((command, index) => {
            const rule = this.fromCommand(command)?.rule;
            if (rule && !rule.flags.includes("allowDuplicates")) {
                indexesByRule.set(rule.id, [...(indexesByRule.get(rule.id) ?? []), index]);
            }
        });

        return new Set([...indexesByRule.values()].filter((indexes) => indexes.length > 1).flat());
    }

    /**
     * Builds the command for a rule from the form values, with the rule's title as its caption.
     * The caption is always the title, replacing any caption the command carries: a rule picked from the
     * catalog has no caption of its own, and the list is meant to name the rule rather than show free text.
     */
    public static toCommand(rule: PolicyRule, values: RuleValues): CommandPayload {
        if (!this.isComposite(rule)) {
            const { command, ...parameters } = rule.command;
            return { command, caption: rule.title(), ...parameters, value: rule.toValues(values) };
        }

        const { subcommands: shapes, command, ...parameters } = rule.command;
        const commandValues = rule.toValues(values);
        const subcommands = shapes
            .map((shape, index) => ({ shape, value: commandValues[index] }))
            .filter((entry): entry is { shape: CommandShape; value: string } => entry.value !== null)
            .map(({ shape, value }) => ({ ...shape, value }));
        return { command, caption: rule.title(), ...parameters, subcommands };
    }
}

export default RuleCatalog;
